import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { 
  FaSpinner, FaGamepad, FaTrophy, FaUsers, 
  FaFilter, FaSearch, FaChevronRight
} from 'react-icons/fa';

const GameList = ({ isLightMode }) => {
  const navigate = useNavigate();
  const auth = getAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

    // Enhanced games data with categories and metadata
    const games = [
      { 
        id: 'bricks-mania', 
        name: 'Bricks Mania', 
        image: '/brickMania.svg',
        category: 'arcade',
        description: 'Break bricks and collect power-ups in this thrilling arcade game'
      },
      { 
        id: '2048', 
        name: '2048', 
        image: '/2048game.svg',
        category: 'puzzle',
        description: 'Merge tiles and reach 2048 in this addictive number puzzle'
      },
      { 
        id: 'shooter', 
        name: 'Meteor Blasters', 
        image: '/MeteorBlaster.png',
        category: 'action',
        description: 'Destroy incoming meteors before they reach your planet'
      },
      { 
        id: 'snake', 
        name: 'Snake', 
        image: '/snake-game.png',
        category: 'arcade',
        description: 'Guide the snake to eat food while avoiding collisions'
      },
      { 
        id: 'whack-a-mole', 
        name: 'Whack A Mole', 
        image: '/Whack-A-Mole.svg',
        category: 'arcade',
        description: 'Test your reflexes by whacking moles as they appear'
      },
      { 
        id: 'tetris', 
        name: 'Tetris', 
        image: '/tetris.png',
        category: 'puzzle',
        description: 'Arrange falling blocks to create complete rows'
      },
      { 
        id: 'matching', 
        name: 'Matching Game', 
        image: '/MatchingGame.svg',
        category: 'memory',
        description: 'Find matching pairs of cards in this memory challenge'
      },
    ];

  // Get all available categories
  const categories = ['all', ...new Set(games.map(game => game.category))];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoggedIn(true);
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  // Handle game selection
  const handleGameClick = (gameId) => {
    navigate(`/instructions/${gameId}`);
  };

  // Filter games by category and search term
  const filteredGames = games.filter(game => {
    const matchesCategory = selectedCategory === 'all' || game.category === selectedCategory;
    const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          game.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Reset all filters
  const resetFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
  };

  // Loading state with gaming theme
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background-color)] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="relative inline-block">
            <FaGamepad className="text-6xl text-[var(--interactive-color)] animate-bounce" />
          </div>
          <p className="text-[var(--text-color)] font-bold text-xl">Loading Game Arena...</p>
          <div className="flex justify-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[var(--interactive-color)] animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="w-2 h-2 rounded-full bg-[var(--interactive-color)] animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="w-2 h-2 rounded-full bg-[var(--interactive-color)] animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) return null;

  // Get the appropriate difficulty color with better contrast
  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'easy':
        return 'var(--success-color)'; // Green stays the same
      case 'medium':
        return '#9B6D0F'; // Darker amber color for better contrast with white text
      case 'hard':
        return 'var(--error-color)'; // Red stays the same
      default:
        return 'var(--interactive-color)';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--background-color)] pb-20">
      {/* Hero Section with Animated Background */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[var(--interactive-color)] to-[var(--primary-color)] py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute top-40 left-1/4 w-12 h-12 rounded-full bg-white animate-pulse" style={{ animationDuration: '6s' }} />
          <div className="absolute top-20 right-1/3 w-16 h-16 rounded-full bg-white animate-pulse" style={{ animationDuration: '5s' }} />
          <div className="absolute bottom-10 right-1/4 w-14 h-14 rounded-full bg-white animate-pulse" style={{ animationDuration: '7s' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Game Arena
            </h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Dive into our collection of mind-bending puzzles and thrilling arcade games
            </p>
            
            {/* Search Bar */}
            <div className="mt-10 max-w-md mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search games..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search games"
                  className="w-full py-3 pl-10 pr-4 bg-white/20 backdrop-blur-md border border-white/30 rounded-full text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
                />
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        {/* CHANGED: Stats Cards - Centered, removed ratings */}
        <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            { icon: FaGamepad, label: 'Games', value: '9+', color: 'from-blue-500 to-indigo-600' },
            { icon: FaUsers, label: 'Categories', value: `${categories.length - 1}`, color: 'from-green-500 to-teal-600' },
            { icon: FaTrophy, label: 'Achievements', value: '50+', color: 'from-yellow-500 to-amber-600' },
          ].map((stat, index) => (
            <div 
              key={index} 
              className="bg-[var(--surface-color)] rounded-xl p-5 shadow-lg transform hover:scale-105 transition-all duration-300 border border-[var(--border-color)]"
            >
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="text-white text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-[var(--text-color)]">{stat.value}</p>
                  <p className="text-sm font-medium text-[var(--text-color)] opacity-80">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Category Filter */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center">
            <FaFilter className="text-[var(--interactive-color)] mr-2" />
            <h2 className="text-xl font-bold text-[var(--text-color)]">Categories</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-[var(--interactive-color)] text-white'
                    : 'bg-[var(--surface-color-hover)] text-[var(--text-color)] hover:bg-[var(--interactive-color)]/20'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Game Grid with Improved Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredGames.map((game) => (
            <div
              key={game.id}
              onClick={() => handleGameClick(game.id)}
              className="group relative bg-[var(--surface-color)] rounded-2xl overflow-hidden shadow-lg border border-[var(--border-color)] cursor-pointer transform transition-all duration-300 hover:shadow-xl hover:translate-y-[-4px]"
            >
              {/* Game Banner with Fixed Image */}
              <div className="relative h-56 bg-gradient-to-br from-[var(--surface-color-hover)] to-[var(--background-color)]">
                <img
                  src={game.image}
                  alt={`${game.name} thumbnail`}
                  className="w-full h-full object-contain p-4"
                />
                
                {/* REMOVED: Rating Badge */}
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-[var(--interactive-color)]/70 transition-all duration-300">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGameClick(game.id);
                    }}
                    className="px-6 py-3 bg-white text-[var(--interactive-color)] rounded-lg font-bold transform scale-90 group-hover:scale-100 transition-all duration-300"
                  >
                    Play Now
                  </button>
                </div>
              </div>

              {/* Game Info - Updated Description */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-[var(--text-color)] group-hover:text-[var(--interactive-color)] transition-colors">
                    {game.name}
                  </h3>
                </div>
                
                {/* UPDATED: Description with darker text and larger size */}
                <p className="text-[var(--text-color)] text-base mb-4 line-clamp-2 font-medium">
                  {game.description}
                </p>
                
                {/* Simplified footer - just Play button */}
                <div className="flex justify-end">
                  <button 
                    className="px-4 py-2 bg-[var(--interactive-color)] text-white rounded-lg text-sm font-medium hover:bg-opacity-90 transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGameClick(game.id);
                    }}
                  >
                    Play
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* No Results */}
        {filteredGames.length === 0 && (
          <div className="text-center py-20">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-2xl font-bold text-[var(--text-color)] mb-2">No games found</h3>
            <p className="text-[var(--text-color)] opacity-75">Try adjusting your filters or search term</p>
            <button
              onClick={resetFilters}
              className="mt-6 px-6 py-2 bg-[var(--interactive-color)] text-white rounded-lg hover:bg-opacity-90 transition-all"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameList;